// ==============================|| REACT & THIRD-PARTY IMPORTS ||============================== //
import React, { useEffect, useRef, useState } from 'react';
import FilerobotImageEditor, { TABS, TOOLS } from 'react-filerobot-image-editor';
import 'react-filerobot-image-editor/lib/custom/shapes/FormattedText';

// ==============================|| MUI IMPORTS ||============================== //
import {
    Dialog,
    IconButton,
    Typography,
    Box,
    useTheme,
    CircularProgress,
    DialogTitle,
    Divider
} from '@mui/material';

// ==============================|| ANT DESIGN ICONS IMPORTS ||============================== //
import { CloseOutlined } from '@ant-design/icons';

// ==============================|| STYLE IMPORTS ||============================== //
import "./ImageEditor.css";

// ==============================|| CONTEXT & HOOKS ||============================== //
import { useFileManagerState } from 'hooks/useFileManager';
import { ImageEditorProps } from 'types/CommonFileManager/commonFileManager';



const ImageEditor: React.FC<ImageEditorProps> = ({
    open: propOpen,
    onClose: propOnClose,
    imageFile: propImageFile,
    imageUrl: propImageUrl,
    imageName: propImageName,
    onSave: propOnSave
}) => {
    const state = useFileManagerState();

    const open = propOpen ?? state.imageEditorOpen;
    const onClose = propOnClose ?? (() => {
        state.setImageEditorOpen(false);
        state.setFileForEditor(null);
    });
    const imageFile = propImageFile ?? null;
    const imageUrl = propImageUrl ?? (state.fileForEditor ? (state.imageUrlsMap[state.fileForEditor.id]) : null);
    const imageName = propImageName ?? state.fileForEditor?.name ?? '';
    const onSave = propOnSave ?? state.handleImageEditorSave;
    const theme = useTheme();
    const ImageEditorRef = useRef(null);
    const [imageSource, setImageSource] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        let objectUrl: string | null = null;

        if (open) {
            if (imageFile) {
                objectUrl = URL.createObjectURL(imageFile);
                setImageSource(objectUrl);
            } else if (imageUrl) {
                setImageSource(imageUrl);
            }
        } else {
            setImageSource(null);
        }

        return () => {
            if (objectUrl) {
                URL.revokeObjectURL(objectUrl);
            }
        };
    }, [open, imageFile, imageUrl]);

    if (!open || !imageSource) return null;

    const handleSave = async (savedImageData: any, fileName: string, isSaveAsNew?: boolean) => {
        setLoading(true);
        try {
            await onSave(savedImageData, fileName, isSaveAsNew);
        } catch (error) {
            console.error("Save error:", error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog
            fullWidth
            maxWidth="xl"
            open={open}
            onClose={onClose}
            TransitionProps={{ mountOnEnter: true, unmountOnExit: true }}
            PaperProps={{
                sx: {
                    bgcolor: '#f8f9fa',
                    backgroundImage: 'none',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    height: '90vh',
                    borderRadius: '12px'
                },
                style: {
                    // @ts-ignore
                    '--primary-main': theme.palette.primary.main,
                    // @ts-ignore
                    '--primary-dark': theme.palette.primary.dark
                }
            }}
            disableEnforceFocus
            sx={{
                zIndex: 10000
            }}
        >
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: '#ffffff', py: 1.5, px: 3 }}>
                <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
                    <Typography component="span" sx={{ color: 'primary.main', fontWeight: 700 }}>
                        {imageName ? 'Edit' : 'Image'}
                    </Typography>{' '}
                    {imageName || imageFile?.name || 'Editor'}
                </Typography>
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    sx={{
                        color: 'primary.main',
                        bgcolor: 'grey.100',
                        '&:hover': { bgcolor: 'grey.200' },
                        transition: 'all 0.2s'
                    }}
                >
                    <CloseOutlined style={{ fontSize: '18px' }} />
                </IconButton>
            </DialogTitle>
            <Divider />
            <Box sx={{ flex: 1, position: 'relative', height: 'calc(100% - 64px)', overflow: 'hidden', bgcolor: '#f4f7f9' }}>
                {loading && (
                    <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000, bgcolor: 'rgba(255,255,255,0.8)', backdropFilter: 'blur(4px)' }}>
                        <Box sx={{ textAlign: 'center' }}>
                            <CircularProgress size={48} thickness={4} color="primary" />
                            <Typography variant="body2" sx={{ mt: 2, fontWeight: 600, color: 'primary.main' }}>
                                Saving changes...
                            </Typography>
                        </Box>
                    </Box>
                )}
                <FilerobotImageEditor
                    onBeforeSave={() => true}
                    onSave={(savedImageData: any, imageDesignState: any) => {
                        handleSave(savedImageData, imageDesignState.fullName, false);
                    }}
                    getCurrentImgDataFnRef={ImageEditorRef}
                    removeSaveButton={false}
                    source={imageSource}
                    savingPixelRatio={1}
                    previewPixelRatio={1}
                    defaultSavedImageName={imageName || imageFile?.name || 'edited-image'}
                    resetOnSourceChange={true}
                    annotationsCommon={{
                        fill: '#ff0000'
                    }}

                    moreSaveOptions={[
                        {
                            label: 'Save',
                            icon: `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path><polyline points="17 21 17 13 7 13 7 21"></polyline><polyline points="7 3 7 8 15 8"></polyline></svg>`,
                            onClick: (triggerSaveModal, triggerSave) => {
                                triggerSave((savedImageData: any, imageDesignState: any) => {
                                    handleSave(savedImageData, savedImageData.fullName, false);
                                })
                            },
                        },
                        {
                            label: 'Save As New',
                            icon: `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="12" y1="18" x2="12" y2="12"></line><line x1="9" y1="15" x2="15" y2="15"></line></svg>`,
                            onClick: (triggerSaveModal, triggerSave) => {
                                triggerSaveModal((savedImageData: any, imageDesignState: any) => {
                                    handleSave(savedImageData, savedImageData.fullName, true);
                                })
                            },
                        },
                    ]}
                    onClose={onClose}

                    Text={{
                        text: '',
                        fontFamily: 'Poppins',
                        fonts: [
                            { label: 'Poppins', value: 'Poppins' },
                            { label: 'Tahoma', value: 'Tahoma' },
                            { label: 'Sans-serif', value: 'Sans-serif' },
                            { label: 'Comic Sans', value: 'Comic-sans' },
                        ],
                    }}
                    Rotate={{ angle: 90, componentType: 'slider' }}
                    Crop={{
                        presetsItems: [
                            {
                                titleKey: 'classicTv',
                                descriptionKey: '4:3',
                                ratio: 4 / 3,
                            },
                            {
                                titleKey: 'cinemascope',
                                descriptionKey: '21:9',
                                ratio: 21 / 9,
                            },
                        ],
                        presetsFolders: [
                            {
                                titleKey: 'socialMedia',
                                groups: [
                                    {
                                        titleKey: 'facebook',
                                        items: [
                                            {
                                                titleKey: 'profile',
                                                width: 180,
                                                height: 180,
                                                descriptionKey: 'fbProfileSize',
                                            },
                                            {
                                                titleKey: 'coverPhoto',
                                                width: 820,
                                                height: 312,
                                                descriptionKey: 'fbCoverPhotoSize',
                                            },
                                        ],
                                    },
                                ],
                            },
                        ],
                    }}
                    tabsIds={[TABS.ADJUST, TABS.ANNOTATE, TABS.WATERMARK, TABS.FINETUNE, TABS.RESIZE, TABS.FILTERS]}
                    defaultTabId={TABS.ADJUST}
                    defaultToolId={TOOLS.CROP}
                    avoidChangesNotSavedAlertOnLeave={true}
                    theme={{
                        palette: {
                            'bg-primary': '#ffffff',
                            'bg-secondary': '#f9fafb',
                            'bg-primary-active': '#f3f4f6',
                            'accent-primary': theme.palette.primary.main,
                            'accent-primary-hover': theme.palette.primary.dark,
                            'accent-primary-active': theme.palette.primary.dark,
                            'accent-primary-disabled': theme.palette.grey[400],
                            'link-primary': theme.palette.primary.main,
                            'link-hover': theme.palette.primary.dark,
                            'text-primary': '#111827',
                            'text-secondary': '#4b5563',
                            'borders-primary': '#e5e7eb',
                            'borders-secondary': '#f3f4f6',
                            'icons-primary': '#4b5563',
                            'icons-secondary': '#9ca3af',
                            'btn-primary-text': '#ffffff',
                            'bg-btn-primary': theme.palette.primary.main,
                        },
                        typography: {
                            fontFamily: theme.typography.fontFamily || 'Inter, sans-serif',
                            fontSize: '13px',
                        }
                    }}
                />
            </Box>
        </Dialog>
    );
};

export default ImageEditor;
